import { SupportedLocales } from "@app/shared/utils/locales";
import { Textfield } from "@design-system/Inputs/Textfield";
import { FlexCol } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { useMemo, useState, type ChangeEvent, type FC } from "react";
import { useTemplateQuestionGroups } from "../../data";
import { useSupplierQuestionsGroupSelectTranslations } from "./i18n";

export const QuestionGroupCreateModal: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onQuestionGroupCreate: ({
        englishName,
        frenchName,
    }: {
        englishName?: string;
        frenchName?: string;
    }) => void;
    availableLocales: SupportedLocales[];
}> = ({ open, onOpenChange, onQuestionGroupCreate, availableLocales }) => {
    const { templateQuestionGroups } = useTemplateQuestionGroups();

    const { t, keys } = useSupplierQuestionsGroupSelectTranslations();

    const [englishName, setEnglishName] = useState<string | undefined>(
        undefined,
    );
    const [frenchName, setFrenchName] = useState<string | undefined>(undefined);

    const handleEnglishNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setEnglishName(e.target.value);
    };
    const handleFrenchNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setFrenchName(e.target.value);
    };

    const handleOpenChange = (open: boolean) => {
        setEnglishName(undefined);
        setFrenchName(undefined);
        onOpenChange(open);
    };

    const showEnglishName = useMemo(() => {
        return availableLocales.includes(SupportedLocales.EN);
    }, [availableLocales]);

    const showFrenchName = useMemo(() => {
        return availableLocales.includes(SupportedLocales.FR);
    }, [availableLocales]);

    const isEnglishNameUnique = useMemo(() => {
        if (!englishName) {
            return true;
        }
        return !templateQuestionGroups.some(
            (group) =>
                group.name?.en?.toLowerCase() === englishName?.toLowerCase(),
        );
    }, [templateQuestionGroups, englishName]);

    const isFrenchNameUnique = useMemo(() => {
        if (!frenchName) {
            return true;
        }
        return !templateQuestionGroups.some(
            (group) =>
                group.name?.fr?.toLowerCase() === frenchName?.toLowerCase(),
        );
    }, [templateQuestionGroups, frenchName]);

    const englishNameError = useMemo(() => {
        if (!showEnglishName) {
            return undefined;
        }
        if (!isEnglishNameUnique) {
            return t(keys.modal_name_already_exists);
        }
        if (englishName?.trim() === "") {
            return t(keys.modal_name_required);
        }
        return undefined;
    }, [englishName, t, keys, isEnglishNameUnique, showEnglishName]);

    const frenchNameError = useMemo(() => {
        if (!showFrenchName) {
            return undefined;
        }
        if (!isFrenchNameUnique) {
            return t(keys.modal_name_already_exists);
        }
        if (frenchName?.trim() === "") {
            return t(keys.modal_name_required);
        }
        return undefined;
    }, [frenchName, t, keys, isFrenchNameUnique, showFrenchName]);

    const handleSave = () => {
        if (!englishName && showEnglishName) {
            setEnglishName("");
            return;
        }

        if (!frenchName && showFrenchName) {
            setFrenchName("");
            return;
        }

        if (!!englishNameError || !!frenchNameError) {
            return;
        }

        onQuestionGroupCreate({
            englishName,
            frenchName,
        });

        setEnglishName(undefined);
        setFrenchName(undefined);
        onOpenChange(false);
    };

    return (
        <Modal open={open} onOpenChange={handleOpenChange}>
            <Modal.Overlay centerContent scrollable>
                <Modal.Content className="max-w-[800px]">
                    <Modal.Header>
                        <Modal.Title>{t(keys.modal_title)}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <FlexCol gap="4" className="max-w-[400px]">
                            {showEnglishName && (
                                <Textfield
                                    value={englishName}
                                    onChange={handleEnglishNameChange}
                                    placeholder={t(keys.modal_english_name)}
                                    error={englishNameError}
                                    required
                                />
                            )}
                            {showFrenchName && (
                                <Textfield
                                    value={frenchName}
                                    onChange={handleFrenchNameChange}
                                    placeholder={t(keys.modal_french_name)}
                                    error={frenchNameError}
                                    required
                                />
                            )}
                        </FlexCol>
                    </Modal.Body>
                    <Modal.Footer>
                        <Modal.CancelCTA>
                            {t(keys.modal_cancel)}
                        </Modal.CancelCTA>
                        <Modal.ConfirmCTA preventClose onClick={handleSave}>
                            {t(keys.modal_save)}
                        </Modal.ConfirmCTA>
                    </Modal.Footer>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};
